import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { Table } from 'reactstrap';
import Loader from '../components/Loader/Loader';
import { ButtonSearch } from '../components/Buttons/Buttons';
import ModalDispositivosUsuario from '../components/Modal/ModalDispositivosUsuario';
import UsuariosService from '../axios_services/usuarios.service';
import { AppContext } from '../application/provider';
import './DispositivosConfianza.css';


function DispositivosConfianza() {
    const [state] = useContext(AppContext);
    const [estados_dispositivo] = useState(
        state.catalogos.tipos.filter(t => t.categoria_id == 44 && [4, 5, 6].includes(Number(t.tipo_id)))
    );
    const [dispositivos, setDispositivos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [nombre, setNombre] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

    const initialFetchDone = useRef(false);

    useEffect(() => {
        if (!initialFetchDone.current) {
            cargarDispositivos();
            initialFetchDone.current = true;
        }
    }, []);

    const cargarDispositivos = async () => {
        setIsLoading(true);
        try {
            const data = await UsuariosService.getDispositivosConfianza();
            setDispositivos(data || []);
        } catch (error) {
            console.error(error);
            setDispositivos([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEliminar = async (dispositivo) => {
        if (!window.confirm(`¿Está seguro de eliminar el dispositivo: ${dispositivo.nombre_dispositivo}?\nEl usuario deberá registrarlo nuevamente al iniciar sesión.`)) return;

        setIsLoading(true);
        try {
            await UsuariosService.deleteDispositivo(dispositivo.dispositivo_id);
            setDispositivos(prev => prev.filter(d => d.dispositivo_id !== dispositivo.dispositivo_id));
        } catch (error) {
            alert('Error al eliminar el dispositivo.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggle = async (dispositivo_id) => {
        try {
            const res = await UsuariosService.toggleDispositivoActivo(dispositivo_id);
            setDispositivos(prev => prev.map(d =>
                d.dispositivo_id === dispositivo_id ? { ...d, activo: res.activo } : d
            ));
        } catch (error) {
            alert('Error al cambiar el estado del dispositivo.');
        }
    };

    const formatFecha = (f) => {
        if (!f) return '—';
        return new Date(f).toLocaleString('es-PE', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const usuariosAgrupados = useMemo(() => {
        const grupos = {};
        dispositivos.forEach(d => {
            if (!grupos[d.usuario_id]) {
                grupos[d.usuario_id] = {
                    usuario_id: d.usuario_id,
                    codigo_usuario: d.codigo_usuario,
                    nombre_completo: d.nombre_completo,
                    perfil_id: d.perfil_id,
                    dispositivos: []
                };
            }
            grupos[d.usuario_id].dispositivos.push(d);
        });
        return Object.values(grupos);
    }, [dispositivos]);

    const usuariosFiltrados = useMemo(() => {
        if (!nombre) return usuariosAgrupados;
        const q = nombre.toLowerCase();
        return usuariosAgrupados.filter(u =>
            u.nombre_completo.toLowerCase().includes(q) ||
            u.codigo_usuario.toLowerCase().includes(q)
        );
    }, [usuariosAgrupados, nombre]);

    const handleVerDispositivos = (usuario) => {
        setUsuarioSeleccionado(usuario);
        setModalOpen(true);
    };

    if (isLoading) return <Loader />;

    return (
        <div className="dispositivos-confianza-container dispositivos-scope">
            <h1>DISPOSITIVOS DE CONFIANZA</h1>

            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="nombre_disp">Nombre / Código:</label>
                    <input
                        id="nombre_disp"
                        type="text"
                        value={nombre}
                        placeholder="Buscar usuario..."
                        onChange={(e) => setNombre(e.target.value)}
                    />
                </div>
            </div>

            <ButtonSearch onClick={cargarDispositivos} isLoading={isLoading}></ButtonSearch>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Código</th>
                            <th>Usuario</th>
                            <th>Total Dispositivos</th>
                            <th>Autorizados</th>
                            <th>Desconocidos</th>
                            <th>Último acceso</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usuariosFiltrados.length > 0 ? (
                            usuariosFiltrados.map((u, idx) => {
                                const autorizados = u.dispositivos.filter(d => Number(d.activo) === 6).length;
                                const desconocidos = u.dispositivos.filter(d => Number(d.activo) === 5).length;
                                const ultimoAcceso = u.dispositivos.reduce((max, d) => {
                                    const fecha = new Date(d.ultimo_acceso);
                                    return fecha > max ? fecha : max;
                                }, new Date(0));
                                return (
                                    <tr key={u.usuario_id}>
                                        <td>{idx + 1}</td>
                                        <td>{u.codigo_usuario}</td>
                                        <td>{u.nombre_completo}</td>
                                        <td style={{ textAlign: 'center' }}>{u.dispositivos.length}</td>
                                        <td style={{ textAlign: 'center' }}><span className="badge-autorizado">{autorizados}</span></td>
                                        <td style={{ textAlign: 'center' }}><span className="badge-desconocido">{desconocidos}</span></td>
                                        <td>{formatFecha(ultimoAcceso)}</td>
                                        <td>
                                            <button className="btn-ver-dispositivos" onClick={() => handleVerDispositivos(u)}>
                                                Ver dispositivos
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={8} className="td-sin-registros">
                                    No se encontraron usuarios con dispositivos registrados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalDispositivosUsuario
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                usuario={usuarioSeleccionado}
                dispositivos={usuarioSeleccionado?.dispositivos || []}
                onEliminar={handleEliminar}
                onToggle={handleToggle}
                formatFecha={formatFecha}
                estadosDispositivo={estados_dispositivo}
            />
        </div >
    );
}

export default DispositivosConfianza;